<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12">
          <b-card>
            <div slot="header">
              <b-row>
                <b-col cols="6">
                  <span>{{ 'invoices_new_title' | translate('global') }}</span>
                </b-col>
              </b-row>
            </div>
            <div v-if="spinners.contentIsLoading" class="text-center">
              <b-spinner type="grow" variant="primary"/>
            </div>
            <div v-else>
              <b-row>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'supplier' | translate('global')">
                    <treeselect value-format="object"
                                :default-expand-level="1" :placeholder="'supplier_select' | translate('global')"
                                instanceId="loadSuppliers" :class="{ 'treeselect-error' : errorMsg.supplier_id }"
                                :load-options="loadOptions" :async="true" :cacheOptions="false"
                                :searchPromptText="'supplier_autocomplete' | translate('global')"
                                :noResultsText="'no_results_found' | translate('global')"
                                v-model="selectedSupplier"/>
                    <span class="error-text" v-if="errorMsg.supplier_id">{{ errorMsg.supplier_id[0] }}</span>
                  </b-form-group>
                </b-col>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'structure' | translate('global')">
                    <treeselect :class="{ 'treeselect-error' : errorMsg.structures }"
                                :multiple="true"
                                :placeholder=" 'select_structure' | translate('global') "
                                :options="structureOptions" v-model="invoice.structures"/>
                    <span class="error-text" v-if="errorMsg.structures">{{ errorMsg.structures[0] }}</span>
                  </b-form-group>
                </b-col>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'invoice_number' | translate('global')">
                    <b-form-input :placeholder="'invoice_number_placeholder' | translate('global')"
                                  :class="{ 'is-invalid' : errorMsg.number }"
                                  v-model="invoice.number"/>
                    <span class="error-text" v-if="errorMsg.number">{{ errorMsg.number[0] }}</span>
                  </b-form-group>
                </b-col>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'invoice_issue_at' | translate('global')">
                    <datepicker :format="'DD.MM.YYYY'" :value-type="'format'"
                                :input-class="'datepicker-input form-control'"
                                :class="{ 'date-picker-error': errorMsg.issue_at }"
                                :placeholder="'invoice_issue_at_placeholder' | translate('global')"
                                v-model="invoice.issue_at"/>
                    <span class="error-text" v-if="errorMsg.issue_at">{{errorMsg.issue_at[0]}}</span>
                  </b-form-group>
                </b-col>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'invoice_payment_term' | translate('global')">
                    <treeselect value-format="object"
                                :class="{ 'treeselect-error' : errorMsg.payment_terms_classifier_id }"
                                :placeholder=" 'invoice_payment_term_placeholder' | translate('global') "
                                :options="paymentTermsOptions" v-model="selectedPaymentTerm"/>
                    <span class="error-text" v-if="errorMsg.payment_terms_classifier_id">
                      {{ errorMsg.payment_terms_classifier_id[0] }}
                    </span>
                  </b-form-group>
                </b-col>
                <b-col cols="6" xl="4">
                  <b-form-group :label="'sum' | translate('global')">
                    <b-form-input type="number" min="0"  :placeholder=" 'add_sum' | translate('global')"
                                  :class="{ 'is-invalid' : errorMsg.sum }"
                                  v-model="invoice.sum"/>
                    <span class="error-text" v-if="errorMsg.sum">{{ errorMsg.sum[0] }}</span>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="'invoice_description' | translate('global')">
                    <b-form-textarea :placeholder=" 'add_comment' | translate('global') " rows="3" max-rows="6"
                                     :class="{ 'is-invalid' : errorMsg.comment }" v-model="invoice.comment"/>
                    <div class="invalid-feedback" v-if="errorMsg.comment">{{ errorMsg.comment[0] }}</div>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="'attachments_comment' | translate('global')">
                    <b-form-textarea rows="3" max-rows="6"
                                     :placeholder=" 'add_attachments_comment' | translate('global') "
                                     :class="{ 'is-invalid' : errorMsg.attachments_comment }"
                                     v-model="invoice.attachments_comment"/>
                    <div class="invalid-feedback" v-if="errorMsg.attachments_comment">
                      {{ errorMsg.attachments_comment[0] }}
                    </div>
                  </b-form-group>
                  <Attachments :isNew="true" :newInvoiceId="newInvoiceId"/>
                </b-col>
              </b-row>
            </div>
            <div slot="footer">
              <b-button type="submit" variant="success" @click="onSave()" :disabled="spinners.isSaving">
                <b-spinner v-if="spinners.isSaving" type="grow" small/>
                {{ 'save' | translate('global') }}
              </b-button>
              <b-button :to="{ name: 'Invoices' }" type="reset" variant="secondary" class="ml-2">
                {{ 'cancel' | translate('global') }}
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import forEach from 'lodash.foreach';
import VuexTypes from '@/store/types/index';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Datepicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/lv';
import Services from '@/services/index';
import Attachments from './Attachments/Attachments.vue';

const InvoiceTypes = VuexTypes.invoices;
const SuppliersService = Services.suppliers;

export default {
  mounted() {
    this.getData();
    if (this.$route.query.supplier) {
      this.loadSupplierById(this.$route.query.supplier);
    }
    if (this.$route.query.structure) {
      this.invoice.structures.push(this.$route.query.structure);
    }
  },
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isSaving: false,
      },
      invoice: {
        supplier_id: null,
        payment_terms_classifier_id: null,
        number: null,
        issue_at: '',
        sum: '',
        comment: '',
        attachments_comment: '',
        structures: [],
      },
      selectedPaymentTerm: null,
      selectedSupplier: null,
      structureOptions: [],
      paymentTermsOptions: [],
      newInvoiceId: null,
      errorMsg: {}
    };
  },
  components: { Treeselect, Datepicker, Attachments },
  computed: {
    ...mapGetters('Invoices', [
      'invoiceCreate',
      'invoiceClassifiers',
    ]),
  },
  watch: {
    invoiceClassifiers: {
      handler: function () {
        if (this.invoiceClassifiers.status === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillClassifiers();
        }
      },
      deep: true
    },
    invoiceCreate: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.invoiceCreate.status === statuses.LOADED) {
          this.newInvoiceId = this.invoiceCreate.data.id;
        } else if (this.invoiceCreate.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.invoiceCreate.data.data.errors;
        }
      },
      deep: true
    },
    selectedSupplier: function () {
      this.invoice['supplier_id'] = this.selectedSupplier ? this.selectedSupplier['id'] : null;

      this.selectedPaymentTerm = this.selectedSupplier && this.selectedSupplier['paymentTermsClassifier'] ? {
        id: this.selectedSupplier['paymentTermsClassifier']['id'],
        label: this.selectedSupplier['paymentTermsClassifier']['name']
      } : null;
    },
    selectedPaymentTerm: function () {
      this.invoice['payment_terms_classifier_id'] = this.selectedPaymentTerm
        ? this.selectedPaymentTerm['id'] : null;
    },
  },
  methods: {
    ...mapActions('Invoices', [
      InvoiceTypes.INVOICE_CREATE,
      InvoiceTypes.INVOICE_CLASSIFIERS,
    ]),
    getData() {
      this.INVOICE_CLASSIFIERS();
    },
    fillClassifiers() {
      this.structureOptions = this.fillTreeselectDropdown(this.invoiceClassifiers.data['structures'], 'id', 'name');
      this.paymentTermsOptions = this.fillTreeselectDropdown(
        this.invoiceClassifiers.data['paymentTermsClassifier'], 'id', 'name'
      );
    },
    onSave() {
      this.spinners.isSaving = true;
      this.INVOICE_CREATE(this.invoice);
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadSuppliers(name) {
      const param = name || '';
      const data = await SuppliersService.search(param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: item['name'],
          paymentTermsClassifier: item['payment_terms_classifier'] ? {
            id: item['payment_terms_classifier']['id'],
            name: item['payment_terms_classifier']['name']
          } : null
        });
      });
      return options;
    },
    async loadSupplierById(id) {
      const data = await SuppliersService.search(null, id);

      if (data.data.data && data.data.data.length > 0) {
        const item = data.data.data[0];
        this.selectedSupplier = {
          id: item['id'],
          label: item['name'],
          paymentTermsClassifier: item['payment_terms_classifier'] ? {
            id: item['payment_terms_classifier']['id'],
            name: item['payment_terms_classifier']['name']
          } : null
        };
      }
    }
  }
};
</script>
